import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useCredits } from '../../hooks/useCredits';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { NotificationContent } from './NotificationContent';
import './NotificationBell.css';

const SEEN_STATE_KEY = 'notification_seen_state';

/**
 * ヘッダー用通知ベルアイコン
 * 期限付きポイントの失効・お知らせのいずれかがある場合にバッジを表示し、
 * クリックでポップアップを表示
 */
export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { data: credits } = useCredits();
  const { data: announcements } = useAnnouncements();

  // 確認済みの状態スナップショット
  const [seenState, setSeenState] = useState<string | null>(() => {
    return localStorage.getItem(SEEN_STATE_KEY);
  });

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        buttonRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 失効予定があるかどうか
  const hasExpiringSoon = credits?.expiring_soon && credits.expiring_soon.amount > 0;

  // お知らせがあるか
  const hasAnnouncements = announcements && announcements.length > 0;

  // 現在の通知状態のキーを生成
  const currentStateKey = useMemo(() => {
    return JSON.stringify({
      expiring: hasExpiringSoon ? credits?.expiring_soon?.amount : 0,
      latestAnnouncementId: announcements?.[0]?.id || null,
    });
  }, [hasExpiringSoon, credits?.expiring_soon?.amount, announcements]);

  // 通知があるかどうか（状態が確認済みと異なる場合のみ）
  const hasNotifications = useMemo(() => {
    const hasAnyNotification = hasExpiringSoon || hasAnnouncements;
    if (!hasAnyNotification) return false;
    // 確認済みの状態と現在の状態が同じならバッジを表示しない
    if (seenState === currentStateKey) return false;
    return true;
  }, [hasExpiringSoon, hasAnnouncements, seenState, currentStateKey]);

  // 通知ポップアップを開いた時に状態を保存
  const markAsSeen = useCallback(() => {
    localStorage.setItem(SEEN_STATE_KEY, currentStateKey);
    setSeenState(currentStateKey);
  }, [currentStateKey]);

  // ボタンクリック時の処理
  const handleButtonClick = () => {
    const wasOpen = isOpen;
    setIsOpen(!isOpen);
    // 開く時に既読にする
    if (!wasOpen) {
      markAsSeen();
    }
  };

  return (
    <div className="notification-bell-container">
      <button
        ref={buttonRef}
        className={`notification-bell-button ${hasNotifications ? 'has-notification' : ''}`}
        onClick={handleButtonClick}
        aria-label="通知"
      >
        <img
          src="/alert_bell_notification_2.svg"
          alt="通知"
          className="notification-bell-icon"
        />
        {hasNotifications && <span className="notification-badge" />}
      </button>

      {isOpen && (
        <div ref={popupRef} className="modal-surface notification-popup">
          <NotificationContent
            expiringSoon={credits?.expiring_soon}
            announcements={announcements}
            onLinkClick={() => setIsOpen(false)}
            onMarkAllAsRead={markAsSeen}
          />
        </div>
      )}
    </div>
  );
}
